import { useNavigate } from 'react-router';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Aguardar,
  BotaoLink,
  CabecalhoPagina,
  classeTd,
  classeTh,
  Etiqueta,
  EstadoVazio,
  Tabela,
} from '../components/ui/index.js';
import { formatarData } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Avaliacao } from '../dados/fonte.js';

export type Situacao = 'planejada' | 'andamento' | 'fechada';

/** Situacao da visita, derivada das avaliacoes (nunca gravada). */
export function situacaoDaVisita(avaliacoes: readonly Avaliacao[], visita?: { closed_at: string | null }): Situacao {
  if (visita?.closed_at) return 'fechada';
  if (avaliacoes.length > 0 && avaliacoes.every((a) => a.status === 'fechada' || a.status === 'validada')) return 'fechada';
  if (avaliacoes.every((a) => a.status === 'rascunho' && !a.started_at)) return 'planejada';
  return 'andamento';
}

export const ROTULO_SITUACAO: Record<Situacao, string> = {
  planejada: 'Planejada',
  andamento: 'Em andamento',
  fechada: 'Fechada',
};

/** Visitas do haras atual. */
export function Visitas() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const navegar = useNavigate();
  const consulta = useConsulta(
    async (f) => {
      const [haras, visitas, avaliacoes] = await Promise.all([
        f.haras(),
        f.cadastro ? f.cadastro.visitas() : Promise.resolve([]),
        f.avaliacoes(),
      ]);
      return { haras, visitas, avaliacoes };
    },
    [],
  );

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {({ haras, visitas, avaliacoes }) => (
        <div className="space-y-6">
          <CabecalhoPagina
            sobretitulo={haras?.name}
            titulo="Visitas"
            meta={<span>Planeje quais animais serão avaliados em cada visita ao haras.</span>}
            acoes={
              haras && (
                <BotaoLink variante="principal" to="/visitas/nova">
                  Nova visita
                </BotaoLink>
              )
            }
          />
          {!haras ? (
            <EstadoVazio
              titulo="Nenhum haras cadastrado"
              acao={
                <BotaoLink variante="principal" to="/haras/novo">
                  Cadastrar haras
                </BotaoLink>
              }
            />
          ) : visitas.length === 0 ? (
            <EstadoVazio
              titulo="Nenhuma visita planejada"
              acao={
                <BotaoLink variante="principal" to="/visitas/nova">
                  Planejar a primeira visita
                </BotaoLink>
              }
            >
              A pré-visita define os animais e o nível de avaliação de cada um, e deixa tudo no tablet para o dia.
            </EstadoVazio>
          ) : (
            <div className="overflow-hidden rounded-(--radius-painel) border border-borda bg-superficie">
              <Tabela rotulo="Visitas">
                <thead>
                  <tr>
                    <th scope="col" className={`${classeTh} w-16 text-right`}>
                      Nº
                    </th>
                    <th scope="col" className={classeTh}>
                      Data
                    </th>
                    <th scope="col" className={`${classeTh} text-right`}>
                      Animais
                    </th>
                    <th scope="col" className={`${classeTh} hidden text-right sm:table-cell`}>
                      Completos
                    </th>
                    <th scope="col" className={`${classeTh} hidden text-right sm:table-cell`}>
                      Censo
                    </th>
                    <th scope="col" className={classeTh}>
                      Situação
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visitas.map((v) => {
                    const daVisita = avaliacoes.filter((a) => a.visit_id === v.id);
                    const situacao = situacaoDaVisita(daVisita, v);
                    return (
                      <tr
                        key={v.id}
                        onClick={() => navegar(`/visitas/${v.id}`)}
                        className="cursor-pointer transition-colors hover:bg-destaque-fundo/60"
                      >
                        <td className={`${classeTd} numero text-right font-semibold`}>{v.visit_number}</td>
                        <td className={`${classeTd} numero`}>{formatarData(v.visit_date)}</td>
                        <td className={`${classeTd} numero text-right`}>{daVisita.length}</td>
                        <td className={`${classeTd} numero hidden text-right text-apagado sm:table-cell`}>
                          {daVisita.filter((a) => a.protocol_level >= 2).length}
                        </td>
                        <td className={`${classeTd} numero hidden text-right text-apagado sm:table-cell`}>
                          {daVisita.filter((a) => a.protocol_level === 1).length}
                        </td>
                        <td className={classeTd}>
                          <Etiqueta tom={situacao === 'planejada' ? 'destaque' : situacao === 'fechada' ? 'ok' : 'aviso'}>
                            {ROTULO_SITUACAO[situacao]}
                          </Etiqueta>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Tabela>
            </div>
          )}
        </div>
      )}
    </Aguardar>
  );
}
